import { PaymentSystemPublic } from "@scrile/api-provider/dist/api/PaymentSystemsProvider";
import { PurchaseSubjectTypeEnum } from "@scrile/api-provider/dist/api/PurchaseTypeProvider";
import { RecurringBillingScheduleSubjectTypeEnum } from "@scrile/api-provider/dist/api/RecurringBillingScheduleProvider";
import { Subject } from "@scrile/api-provider/dist/api/SubjectProvider";
import providers from "@/lib/api";
import paymentComponent from "./CCBillFlexForm.vue";
import { createRejectError } from "../../integrations/utils";
import { IntegrationModule } from "../types";

const TIMEOUT_TO_CLOSE = 1000 * 60 * 5;

type RequestState = "success" | "canceled" | "error";
const open = (w: number, h: number) => {
  const left = screen.width / 2 - w / 2,
    top = screen.height / 2 - h / 2;
  const config = `width=${w}, height=${h}, top=${top}, left=${left}, toolbar=0, location=1, directories=0, status=0, menubar=0, scrollbars=1, resizable=0, copyhistory=0,`;
  return window.open("about:blank", "_blank", config);
};

const waitRequestStatus = (getUrl: () => Promise<string>) =>
  new Promise<RequestState>(async (resolve, reject) => {
    if ((window as any).setCCBillRequestStatus) {
      reject(new Error("Other payment in process"));
      return;
    }
    let requestStatus: "success" | "canceled";
    (window as any).setCCBillRequestStatus = (status: "success" | "canceled") => {
      requestStatus = status;
    };
    const tab = open(980, 900);
    if (!tab) {
      delete (window as any).setCCBillRequestStatus;
      reject(new Error("Cant create payment window"));
      return;
    }
    tab.location.href = await getUrl();
    const interval = setInterval(() => {
      if (tab.closed) {
        if (requestStatus === "success") {
          resolve("success");
        } else if (requestStatus === "canceled") {
          resolve("canceled");
        } else {
          resolve("error");
        }
        delete (window as any).setCCBillRequestStatus;
        clearInterval(interval);
      }
    }, 500);
  });

const makePurchase = async (getUrl: () => Promise<string>) => {
  const requestState = await waitRequestStatus(getUrl);
  if (requestState === "canceled") {
    throw new Error("Payment canceled");
  } else if (requestState === "error") {
    throw new Error("Cant verify transaction");
  }
  return true;
};

interface PaymentData {
  subscriptionSubject?: Subject<RecurringBillingScheduleSubjectTypeEnum>;
  purchaseSubject?: Subject<PurchaseSubjectTypeEnum>;
}
const module: IntegrationModule<PaymentData> = {
  paymentComponent,
  purchase: (data, paymentDetails) =>
    new Promise(async (resolve, reject) => {
      try {
        let timeout: ReturnType<typeof setTimeout>;
        const subscription = await providers.PaymentSystemsProvider.subscribeDepositPurchase(t => {
          timeout && clearTimeout(timeout);
          subscription.unsubscribe();
          resolve({
            status: true,
            subjectPurchased: !!paymentDetails?.purchaseSubject,
            transaction: t,
          });
        });
        try {
          await makePurchase(() =>
            providers.CCBillFlexFormPurchaseProvider.getDepositUrl({
              data: { subject: paymentDetails?.purchaseSubject, ...data },
            })
          ).catch(e => {
            if (e.message === "Cant verify transaction") {
              timeout = setTimeout(() => {
                subscription.unsubscribe();
                reject(createRejectError(e));
              }, TIMEOUT_TO_CLOSE);
            } else throw e;
          });
        } catch (e) {
          subscription.unsubscribe();
          reject(createRejectError(e));
        }
      } catch (e) {
        reject(createRejectError(e));
      }
    }),
  subscribe: (data, paymentDetails) =>
    new Promise(async (resolve, reject) => {
      try {
        let timeout: ReturnType<typeof setTimeout>;
        const subscription = await providers.PaymentSystemsProvider.subscribeRecurringPurchase(s => {
          timeout && clearTimeout(timeout);
          subscription.unsubscribe();
          resolve({
            status: true,
            subjectPurchased: false,
            subscription: s,
          });
        });
        try {
          if (paymentDetails?.subscriptionSubject) {
            await makePurchase(() =>
              providers.CCBillFlexFormPurchaseProvider.getRecurringUrl({
                data: {
                  ...data,
                  ...(paymentDetails?.subscriptionSubject ? { subject: paymentDetails?.subscriptionSubject } : {}),
                },
              })
            ).catch(e => {
              if (e.message === "Cant verify transaction") {
                timeout = setTimeout(() => {
                  subscription.unsubscribe();
                  reject(createRejectError(e));
                }, TIMEOUT_TO_CLOSE);
              } else throw e;
            });
          }
        } catch (e) {
          subscription.unsubscribe();
          reject(createRejectError(e));
        }
      } catch (e) {
        reject(createRejectError(e));
      }
    }),
  allowSaveCard: false,
};

export const selector = (system: PaymentSystemPublic | null): boolean => {
  return system?.type === "ccbill-flexform" ?? false;
};

export default module;
